import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Box, Select } from '@packages/shared';
import { ArrowMediumDown32 } from '@packages/themes/icons';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { useSorting } from '../../hooks/useSorting';
import { usePaging } from '../../hooks/usePaging';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import type { PagingMode, DesktopDisplayMode } from '../../types';
import { DisplaySettingsDesktopPagination } from '../DisplaySettingsDesktopPagination';

export const DisplaySettingsDesktopFragment = graphql(/* GraphQL */ `
  fragment DisplaySettingsDesktopFragment on SearchProductResult {
    ...UseDisplayModeFragment
    ...UsePagingFragment
    ...UseSortingFragment
    ...DisplaySettingsDesktopPaginationFragment
    availableSortingOrders {
      key
      displayName
    }
  }
`);

export type DisplaySettingsDesktopProps = {
  maskedData: FragmentType<typeof DisplaySettingsDesktopFragment>;
};

const messages = defineMessages({
  displayModeLabel: {
    id: 'search.productList.displaySettings.displayMode.label',
    defaultMessage: 'Ansicht wechseln',
  },
  displayModeCards: {
    id: 'search.productList.displaySettings.displayModes.cards',
    defaultMessage: 'Kleine Bilder',
  },
  displayModeLargeCards: {
    id: 'search.productList.displaySettings.displayModes.largeCards',
    defaultMessage: 'Große Bilder',
  },
  displayModeMoreInformation: {
    id: 'search.productList.displaySettings.displayModes.moreInformation',
    defaultMessage: 'Mehr Informationen',
  },
  pagingModeInfinite: {
    id: 'search.productList.displaySettings.pagingMode.infinite',
    defaultMessage: 'Alle anzeigen',
  },
  pagingModeLimited: {
    id: 'search.productList.displaySettings.pagingMode.limited',
    defaultMessage: '72 Artikel pro Seite',
  },
  sortingModeLabel: {
    id: 'search.productList.displaySettings.sortingMode.label',
    defaultMessage: 'Sortieren',
  },
});

/**
 * Component to allow the user to change the display settings for the product list
 * */
export const DisplaySettingsDesktop: FC<DisplaySettingsDesktopProps> = ({ maskedData }) => {
  const data = getFragmentData(DisplaySettingsDesktopFragment, maskedData);
  const { formatMessage } = useIntl();

  const { pagingMode, pageCount, pushPagingMode } = usePaging(data);
  const { displayMode, pushDisplayModeChange } = useDisplayMode(data);
  const { sortingKey, pushSortingChange } = useSorting(data);

  const displayModes = [
    { value: 'cards', label: formatMessage(messages.displayModeCards) },
    { value: 'largeCards', label: formatMessage(messages.displayModeLargeCards) },
    { value: 'moreInformation', label: formatMessage(messages.displayModeMoreInformation) },
  ];

  const sortingItems = data.availableSortingOrders.map(({ key: value, displayName: label }) => ({
    value,
    label,
  }));

  const pagingModeItems = [
    { value: 'limited', label: formatMessage(messages.pagingModeLimited) },
    { value: 'infinite', label: formatMessage(messages.pagingModeInfinite) },
  ];

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, alignItems: 'center' }}
    >
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label={formatMessage(messages.displayModeLabel)}
        items={displayModes}
        value={displayMode}
        onChange={(event) => {
          pushDisplayModeChange(event.target.value as DesktopDisplayMode);
        }}
      />
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label=""
        items={pagingModeItems}
        value={pagingMode}
        onChange={(event) => {
          pushPagingMode(event.target.value as PagingMode);
        }}
      />
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label={formatMessage(messages.sortingModeLabel)}
        items={sortingItems}
        value={sortingKey}
        onChange={(event) => {
          pushSortingChange(event.target.value as string);
        }}
      />
      {pagingMode !== 'infinite' && pageCount > 1 && (
        <DisplaySettingsDesktopPagination maskedData={data} />
      )}
    </Box>
  );
};
